export class UnitsGridFormattingHelper {
  public static parseClipboardRows(text: string): readonly (readonly string[])[] {
    return text
      .replace(/\r/g, '')
      .split('\n')
      .map((line) => line.split('\t').map((value) => value.trim()))
      .filter((line) => line.some((value) => value !== ''));
  }

  public static normalizeImportedText(text: string): string {
    const normalizedText = text.replace(/\r/g, '').trim();
    if (!normalizedText) {
      return '';
    }

    if (normalizedText.includes('\t')) {
      return normalizedText
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .join('\n');
    }

    const delimiter = this.detectDelimiter(normalizedText);
    if (delimiter === ',' || delimiter === ';') {
      const parsed = normalizedText
        .split('\n')
        .map((line) => this.parseDelimitedLine(line, delimiter))
        .map((row) => row.map((cell) => (cell ?? '').trim()))
        .filter((row) => row.some((cell) => cell.length > 0));

      return parsed.map((row) => row.join('\t')).join('\n');
    }

    return normalizedText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => line.replace(/\s+/g, '\t'))
      .join('\n');
  }

  private static detectDelimiter(text: string): ',' | ';' | 'whitespace' {
    const sampleLine = text.split('\n').find((line) => line.trim().length > 0) ?? '';
    const commaCount = (sampleLine.match(/,/g) ?? []).length;
    const semicolonCount = (sampleLine.match(/;/g) ?? []).length;

    if (semicolonCount > commaCount && semicolonCount > 0) {
      return ';';
    }

    if (commaCount > 0) {
      return ',';
    }

    return 'whitespace';
  }

  private static parseDelimitedLine(line: string, delimiter: ',' | ';'): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        const nextChar = line[i + 1];
        if (inQuotes && nextChar === '"') {
          current += '"';
          i += 1;
          continue;
        }

        inQuotes = !inQuotes;
        continue;
      }

      if (char === delimiter && !inQuotes) {
        result.push(current);
        current = '';
        continue;
      }

      current += char;
    }

    result.push(current);
    return result;
  }

  public static parseNumericCellValue(
    value: string,
    thousandsDelimiter: string,
    decimalDelimiter: string
  ): number | null | undefined {
    if (value === '') {
      return null;
    }

    let normalizedValue = value;
    if (thousandsDelimiter) {
      normalizedValue = normalizedValue.split(thousandsDelimiter).join('');
    }

    if (decimalDelimiter && decimalDelimiter !== '.') {
      normalizedValue = normalizedValue.split(decimalDelimiter).join('.');
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  public static buildTabDelimitedSelection(
    headers: readonly string[],
    rows: readonly (readonly unknown[])[]
  ): string {
    if (headers.length === 0) {
      return '';
    }

    const headerText = headers.join('\t');
    const rowsText = rows
      .map((row) => row.map((value) => `${value ?? ''}`).join('\t'))
      .join('\n');

    return rowsText ? `${headerText}\n${rowsText}` : headerText;
  }
}
